import { SIZING_FRACTION } from '@postflop/schema';
import { classifyCombo } from '../range/classify';
import { applyElasticity, continueProbFor, raiseProbFor } from '../range/action-model';
import { legalActions } from '../hand/legal';
const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
/** Total chips in the middle (all pots), the pot a bet/raise is sized against. */
const potTotalOf = (state) => state.pots.reduce((sum, p) => sum + p.amount, 0);
/**
 * Bet-size pressure as a pot-fraction proxy, on the SAME 1.0-is-a-standard-bet scale action-model.ts's
 * elasticity curve is calibrated to (a small stab < 1, an overbet > 1). Bucketed to mirror the
 * NarrowFacing pressure ladder so a villain who calls a two-thirds bet in simulation is the same villain
 * whose range a two-thirds bet narrows.
 */
const pressureFromFraction = (fraction) => fraction <= 0.4 ? 0.75 : fraction <= 0.8 ? 1.0 : fraction <= 1.2 ? 1.3 : 1.7;
/** The sizingHabits contexts that apply to a bet/raise on this street (data uses these labels). */
const sizingContexts = (street, isRaise) => {
    if (isRaise)
        return ['raise'];
    switch (street) {
        case 'flop':
            return ['flop_cbet'];
        case 'turn':
            return ['turn_barrel'];
        case 'river':
            return ['river_bet', 'river_value'];
        default:
            return []; // preflop opens/limps are driven by raiseProb, not sizingHabits
    }
};
/**
 * Pick a sizing intent from the archetype's habits for this context. Each habit is an independent
 * propensity (probabilities need not sum to 1): draw against each matching habit in table order and
 * take the first that fires, else the documented fallback. Deterministic given `rng`.
 */
const pickSizingIntent = (arch, contexts, rng, fallback) => {
    for (const habit of arch.postflop.sizingHabits) {
        if (contexts.includes(habit.context) && rng.next() < habit.prob)
            return habit.intent;
    }
    return fallback;
};
const legalOf = (legal, kind) => legal.find((l) => l.kind === kind);
/**
 * Turn an aggressive intent (bet when unbet, raise when facing a bet) into a concrete, legal Action.
 * Falls back to a jam when the raise option is closed but chips remain, or to the passive action when
 * no aggression is legal at all (defensive - the caller only asks to be aggressive when it is legal).
 */
const buildAggressive = (state, seat, arch, legal, toCall, pot, rng, isRaise) => {
    const player = state.players.find((p) => p.seat === seat);
    if (!player)
        throw new Error(`buildAggressive: no player at seat ${seat}`);
    const shove = player.committedThisStreet + player.stack;
    const kind = isRaise ? 'raise' : 'bet';
    const slot = legalOf(legal, kind);
    const jam = legalOf(legal, 'jam');
    if (!slot) {
        if (jam)
            return { seat, kind: 'jam', amount: shove };
        return { seat, kind: isRaise ? 'call' : 'check' };
    }
    const intent = pickSizingIntent(arch, sizingContexts(state.street, isRaise), rng, isRaise ? 'pot' : 'half');
    if (intent === 'jam')
        return { seat, kind: 'jam', amount: shove };
    const lo = slot.min ?? 0;
    const hi = slot.max ?? shove;
    let to;
    if (intent === 'min') {
        to = lo; // context-computed min-raise / min-bet is exactly the legal floor
    }
    else {
        const fraction = SIZING_FRACTION[intent];
        // Raise convention matches grading/bet-math.ts: call the bet, then bet `fraction` of the pot after
        // that call, sized as a raise-TO amount off the villain's current street commitment.
        //
        // ROUND TO WHOLE DOLLARS AT SOURCE (CX1). `fraction * pot` is the only float source postflop, and
        // without this round a postflop bet emits e.g. `$211.39999999999998`, poisoning every downstream
        // frame's money and breaking chip conservation by rounding artifacts. The PREFLOP path already rounds
        // its raise target (`preflopIntentToAction`: `Math.round(toBB * bb)`, ~line 163); this mirrors it so
        // no engine money is ever fractional. Rounding the target (before the legal clamp) matches preflop's
        // order exactly; the clamp bounds (lo/hi) are whole once all bets are whole, so the result stays whole.
        to = Math.round(isRaise ? player.committedThisStreet + toCall + fraction * (pot + toCall) : player.committedThisStreet + fraction * pot);
    }
    const amount = Math.min(Math.max(to, lo), hi);
    return { seat, kind, amount };
};
/**
 * Legalize a preflop INTENT + bb target into a concrete Action. The strategy layer names the intent; this
 * layer owns legality (it already imports hand/legal, strategy does not). Mirrors PF3's
 * `mechanicalPreflopAction`: the bb target is driven to whole dollars and clamped to the legal band, and
 * an intent the state cannot offer de-escalates to the closest legal action rather than throwing.
 */
const preflopIntentToAction = (state, seat, intent, toBB, legal) => {
    const bb = state.spec.stakes.bb;
    const player = state.players.find((p) => p.seat === seat);
    const has = (k) => legalOf(legal, k);
    const raiseTo = () => {
        const slot = has('raise');
        if (!slot)
            return undefined;
        let to = toBB !== undefined ? Math.round(toBB * bb) : (slot.min ?? bb * 2);
        if (slot.min !== undefined)
            to = Math.max(to, slot.min);
        if (slot.max !== undefined)
            to = Math.min(to, slot.max);
        return { seat, kind: 'raise', amount: to };
    };
    const jam = () => {
        const slot = has('jam');
        return slot ? { seat, kind: 'jam', amount: slot.max ?? player.committedThisStreet + player.stack } : undefined;
    };
    switch (intent) {
        case 'fold':
            // A free check is never folded - folding when it costs nothing is not a leak any archetype has.
            return has('check') ? { seat, kind: 'check' } : has('fold') ? { seat, kind: 'fold' } : { seat, kind: 'call' };
        case 'limp':
        case 'call':
            return has('call') ? { seat, kind: 'call' } : has('check') ? { seat, kind: 'check' } : { seat, kind: 'fold' };
        case 'open':
        case 'three_bet':
        case 'four_bet':
            return raiseTo() ?? jam() ?? (has('call') ? { seat, kind: 'call' } : { seat, kind: 'check' });
        case 'jam':
            return jam() ?? raiseTo() ?? (has('call') ? { seat, kind: 'call' } : { seat, kind: 'check' });
    }
};
/**
 * The villain at `seat` chooses a legal Action for the current HandState. `deal` supplies the villain's
 * hidden hole cards (kept out of HandState until showdown; the scenario engine is the dealer's-eye view
 * that may read them). `rng` is the villain-decision stream - deterministic per seed.
 *
 * `preflopPolicy` (TP1, optional) replaces the preflop leg with the chart + knob driven policy; omitted,
 * preflop keeps running through the postflop action model exactly as before (see VillainPreflopPolicy).
 */
export const scriptVillainAction = (state, seat, arch, deal, rng, preflopPolicy) => {
    const legal = legalActions(state);
    const player = state.players.find((p) => p.seat === seat);
    if (!player)
        throw new Error(`scriptVillainAction: no player at seat ${seat}`);
    const hole = deal.holeCards[seat];
    if (!hole || hole.length !== 2)
        throw new Error(`scriptVillainAction: no hole cards for seat ${seat}`);
    if (state.street === 'preflop' && preflopPolicy) {
        const { intent, toBB } = preflopPolicy(state, seat, arch, hole, rng);
        return preflopIntentToAction(state, seat, intent, toBB, legal);
    }
    const { handClass } = classifyCombo(hole, state.board);
    const toCall = state.currentBet - player.committedThisStreet;
    const pot = potTotalOf(state);
    const elasticity = arch.imbalances.elasticity;
    const contProb = continueProbFor(arch, handClass);
    const rawRaise = raiseProbFor(arch, handClass);
    if (toCall > 0) {
        // Facing a bet: fold / call / raise, priced by how big the bet is relative to the pot.
        const pressure = pressureFromFraction(pot > 0 ? toCall / pot : 1);
        const pContinue = applyElasticity(contProb, elasticity, pressure);
        const raiseLegal = legalOf(legal, 'raise') ?? legalOf(legal, 'jam');
        // A raise into a bet is only ever priced UP (a small bet doesn't make players raise more), so the
        // pressure floors at 1 for the raise leg - matching narrowMultiplier's 'raise' branch.
        const pRaise = raiseLegal ? applyElasticity(rawRaise, elasticity, Math.max(pressure, 1)) : 0;
        const r = rng.next();
        if (pRaise > 0 && r < pRaise)
            return buildAggressive(state, seat, arch, legal, toCall, pot, rng, true);
        if (r < pContinue && legalOf(legal, 'call'))
            return { seat, kind: 'call' };
        return legalOf(legal, 'fold') ? { seat, kind: 'fold' } : { seat, kind: 'call' };
    }
    // Unbet: check or bet. Air/weak holdings bet at the archetype's bluffWeight-scaled frequency (its
    // over/under-bluff tendency), value hands at their raw raiseProb.
    const isBluffy = handClass === 'air' || handClass === 'ace_high' || handClass === 'weak_draw';
    const pBet = clamp01(isBluffy ? rawRaise * arch.postflop.bluffWeight : rawRaise);
    const r = rng.next();
    const canBet = legalOf(legal, 'bet') ?? legalOf(legal, 'jam');
    if (r < pBet && canBet)
        return buildAggressive(state, seat, arch, legal, 0, pot, rng, false);
    return legalOf(legal, 'check') ? { seat, kind: 'check' } : { seat, kind: 'call' };
};
//# sourceMappingURL=villain.js.map